import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  UserCog,
  Lock,
  Activity,
  RefreshCw,
} from 'lucide-react';
import { ROLE_LABELS } from '../../data/mockData';
import { toast } from 'sonner';

const roleColors = {
  admin: 'bg-red-50 text-red-700 border-red-200',
  supreme_manager: 'bg-purple-50 text-purple-700 border-purple-200',
  manager: 'bg-blue-50 text-blue-700 border-blue-200',
  quality_checker: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  field_executive: 'bg-amber-50 text-amber-700 border-amber-200',
  vendor: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  client: 'bg-pink-50 text-pink-700 border-pink-200',
  business_partner: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  universal_qc: 'bg-teal-50 text-teal-700 border-teal-200',
  data_entry_operator: 'bg-orange-50 text-orange-700 border-orange-200',
  vendor_fe: 'bg-sky-50 text-sky-700 border-sky-200',
};

export default function UserPermissionsView({
  permissionsMatrix,
  handleTogglePermission,
  sessionTimeout,
  setSessionTimeout,
  passwordPolicy,
  setPasswordPolicy,
  loginLogs,
}) {
  return (
    <div className="space-y-6">
      {/* Permissions Matrix */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 pb-4 mb-4 gap-3">
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <UserCog className="w-4 h-4 text-indigo-600" /> Dynamic Role Permissions CRUD Matrix
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Stateful mapping of CRUD overrides. Toggling checkboxes modifies default access scopes instantly.
            </p>
          </div>
          <div className="text-right text-[10px] bg-slate-50 border border-slate-200 rounded-lg p-2 font-mono text-slate-500 font-semibold uppercase tracking-wider">
            System Mode: Reactive Simulation
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Role Group</th>
                <th className="px-4 py-3 text-center">Create</th>
                <th className="px-4 py-3 text-center">Edit</th>
                <th className="px-4 py-3 text-center">Delete</th>
                <th className="px-4 py-3 text-center">View</th>
                <th className="px-4 py-3 text-center">Export</th>
                <th className="px-4 py-3 text-center">Assign</th>
                <th className="px-4 py-3 text-center">Approve</th>
                <th className="px-4 py-3 text-center">Reject</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              {Object.entries(permissionsMatrix).map(([roleKey, perms]) => (
                <tr key={roleKey} className="hover:bg-slate-50/30">
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${roleColors[roleKey] || 'bg-slate-50 text-slate-700'}`}>
                      {ROLE_LABELS[roleKey] || roleKey}
                    </span>
                  </td>
                  {['create', 'edit', 'delete', 'view', 'export', 'assign', 'approve', 'reject'].map(permType => (
                    <td key={permType} className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={perms[permType]}
                        disabled={roleKey === 'admin'} // Admin privileges remain static for system safety
                        onChange={() => handleTogglePermission(roleKey, permType)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Password Policy & Session Timeout Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <Lock className="w-4 h-4 text-rose-600" /> System Account Policies
          </h3>
          
          <div className="space-y-4 border-t border-slate-100 pt-4 text-xs">
            <div className="space-y-1.5">
              <Label className="text-slate-600 font-semibold">Active Session Timeout (Minutes)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={sessionTimeout}
                  onChange={(e) => setSessionTimeout(parseInt(e.target.value))}
                  className="bg-slate-50/50 border-slate-200 h-9 font-mono max-w-[120px]"
                />
                <Button variant="outline" className="border-slate-200 h-9" onClick={() => toast.success('Session timeout window saved!')}>
                  Update
                </Button>
              </div>
              <span className="text-[10px] text-slate-400 block mt-1">Inactive users will be forcibly signed out after this timeline.</span>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <Label className="text-slate-600 font-semibold block mb-2">Password Policy Requirements</Label>
              <div className="grid grid-cols-2 gap-2 text-slate-600">
                <label className="flex items-center gap-2 font-medium">
                  <input
                    type="checkbox"
                    checked={passwordPolicy.requireCapital}
                    onChange={(e) => setPasswordPolicy({ ...passwordPolicy, requireCapital: e.target.checked })}
                    className="w-3.5 h-3.5 border-slate-300 text-blue-600 rounded"
                  />
                  <span>Require A-Z Cap Letter</span>
                </label>
                <label className="flex items-center gap-2 font-medium">
                  <input
                    type="checkbox"
                    checked={passwordPolicy.requireNumber}
                    onChange={(e) => setPasswordPolicy({ ...passwordPolicy, requireNumber: e.target.checked })}
                    className="w-3.5 h-3.5 border-slate-300 text-blue-600 rounded"
                  />
                  <span>Require Numbers (0-9)</span>
                </label>
                <label className="flex items-center gap-2 font-medium">
                  <input
                    type="checkbox"
                    checked={passwordPolicy.requireSymbol}
                    onChange={(e) => setPasswordPolicy({ ...passwordPolicy, requireSymbol: e.target.checked })}
                    className="w-3.5 h-3.5 border-slate-300 text-blue-600 rounded"
                  />
                  <span>Require Symbol (!@#)</span>
                </label>
                <div className="flex items-center gap-2 font-medium">
                  <span className="text-[10px] text-slate-400">Min Length:</span>
                  <Input
                    type="number"
                    value={passwordPolicy.minLength}
                    onChange={(e) => setPasswordPolicy({ ...passwordPolicy, minLength: parseInt(e.target.value) })}
                    className="bg-slate-50/50 border-slate-200 h-7 w-12 font-mono p-1 text-center text-[10px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Login Logs */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3 flex flex-col">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-600" /> Active Login Logs
            </h3>
            <Button variant="ghost" size="icon" className="w-6 h-6 text-slate-400 hover:text-slate-800" onClick={() => toast.success('Login logs refreshed!')}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>

          <ScrollArea className="flex-1 max-h-[250px] border border-slate-100 rounded-lg overflow-hidden">
            <table className="w-full text-left border-collapse text-[10px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="px-3 py-2">User (Role)</th>
                  <th className="px-3 py-2">IP & Agent</th>
                  <th className="px-3 py-2">Timestamp</th>
                  <th className="px-3 py-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-600 font-medium">
                {loginLogs.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50/30">
                    <td className="px-3 py-2">
                      <p className="font-semibold text-slate-800">{log.user}</p>
                      <span className="text-[8px] text-slate-400 uppercase font-mono">{log.role}</span>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-mono">{log.ip}</p>
                      <p className="text-[8px] text-slate-400 max-w-[120px] truncate">{log.device}</p>
                    </td>
                    <td className="px-3 py-2 font-mono">{log.timestamp}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${log.status.startsWith('Success') || log.status.startsWith('Created') ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
